import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { Streamdown } from 'streamdown';
import type { AssistantUIMessage } from '@/api/ai/ai.types';
import { AgentMark } from './agent-mark.component';
import {
  AssistantAskUser,
  AssistantAskUserAnswered,
} from './assistant-ask-user.component';
import { AssistantToolResult } from './assistant-tool-result.component';
import { AssistantHistorySidebar } from './assistant-history-sidebar.component';
import { AssistantRating } from './assistant-rating.component';
import { MessageComposeCard } from './message-compose-card.component';
import { ASSISTANT_SUGGESTIONS } from './assistant.constants';
import type { ChatStatus } from './use-assistant-chat';
import { useAssistantChat } from './use-assistant-chat';
import { useAssistantStore } from './use-assistant-store';
import 'streamdown/styles.css';

type MessagePart = AssistantUIMessage['parts'][number];
type ToolPart = Extract<
  MessagePart,
  {
    type:
      | 'tool-searchItems'
      | 'tool-getItem'
      | 'tool-queryBusinessPartners'
      | 'tool-getBusinessPartner'
      | 'tool-getBusinessPartnerOrders'
      | 'tool-getOrder'
      | 'tool-listBusinessPartnerContacts'
      | 'tool-askUser'
      | 'tool-activateSkill'
      | 'tool-message_compose_v1'
      | 'tool-listContactLists'
      | 'tool-createContactList'
      | 'tool-addContactsToContactList'
      | 'tool-addDefaultContactsToContactList'
      | 'dynamic-tool';
  }
>;
type ToolTone = 'running' | 'done' | 'error';

/** Per-tool activity titles, keyed by the typed tool part name. */
const TOOL_TITLES: Record<
  Exclude<ToolPart['type'], 'dynamic-tool'>,
  Record<ToolTone, string>
> = {
  'tool-searchItems': {
    running: 'Katalog wird durchsucht…',
    done: 'Katalog durchsucht',
    error: 'Katalogsuche fehlgeschlagen',
  },
  'tool-getItem': {
    running: 'Artikel wird geladen…',
    done: 'Artikeldetails geladen',
    error: 'Artikel konnte nicht geladen werden',
  },
  'tool-queryBusinessPartners': {
    running: 'Geschäftspartner werden durchsucht…',
    done: 'Geschäftspartner durchsucht',
    error: 'Partnersuche fehlgeschlagen',
  },
  'tool-getBusinessPartner': {
    running: 'Geschäftspartner wird geladen…',
    done: 'Geschäftspartner geladen',
    error: 'Geschäftspartner konnte nicht geladen werden',
  },
  'tool-getBusinessPartnerOrders': {
    running: 'Bestellungen werden geladen…',
    done: 'Bestellungen geladen',
    error: 'Bestellungen konnten nicht geladen werden',
  },
  'tool-getOrder': {
    running: 'Auftrag wird geladen…',
    done: 'Auftragsdetails geladen',
    error: 'Auftrag konnte nicht geladen werden',
  },
  'tool-listBusinessPartnerContacts': {
    running: 'Ansprechpartner werden geladen…',
    done: 'Ansprechpartner geladen',
    error: 'Ansprechpartner konnten nicht geladen werden',
  },
  'tool-askUser': {
    running: 'Rückfrage…',
    done: 'Rückfrage beantwortet',
    error: 'Rückfrage fehlgeschlagen',
  },
  'tool-activateSkill': {
    running: 'Skill wird aktiviert…',
    done: 'Skill aktiviert',
    error: 'Skill-Aktivierung fehlgeschlagen',
  },
  'tool-message_compose_v1': {
    running: 'Nachrichtenentwurf wird vorbereitet…',
    done: 'Nachrichtenentwurf bereit',
    error: 'Nachrichtenentwurf fehlgeschlagen',
  },
  'tool-listContactLists': {
    running: 'Verteiler werden geladen…',
    done: 'Verteiler geladen',
    error: 'Verteiler konnten nicht geladen werden',
  },
  'tool-createContactList': {
    running: 'Verteiler wird angelegt…',
    done: 'Verteiler angelegt',
    error: 'Verteiler konnte nicht angelegt werden',
  },
  'tool-addContactsToContactList': {
    running: 'Kontakte werden hinzugefügt…',
    done: 'Kontakte hinzugefügt',
    error: 'Kontakte konnten nicht hinzugefügt werden',
  },
  'tool-addDefaultContactsToContactList': {
    running: 'Standardkontakte werden hinzugefügt…',
    done: 'Standardkontakte hinzugefügt',
    error: 'Standardkontakte konnten nicht hinzugefügt werden',
  },
};

/** Animated three-dot "thinking" bubble shown before the first token. */
function ThinkingBubble() {
  return (
    <article className="eos-asst-msg is-assistant">
      <span className="eos-asst-msg-avatar" aria-hidden="true">
        <AgentMark />
      </span>
      <div className="eos-asst-msg-main">
        <div className="eos-asst-bubble eos-asst-think" aria-live="polite">
          <ActivityDots />
          <span className="eos-asst-think-label">
            Der Assistent denkt nach…
          </span>
        </div>
      </div>
    </article>
  );
}

/** The three pulsing dots used by both the thinking bubble and activity row. */
function ActivityDots() {
  return (
    <span className="eos-asst-think-dots" aria-hidden="true">
      <span className="eos-asst-think-dot" />
      <span className="eos-asst-think-dot" />
      <span className="eos-asst-think-dot" />
    </span>
  );
}

/** Resolves the live activity label for the in-flight assistant message. */
function activityLabel(
  message: AssistantUIMessage,
  status: ChatStatus,
): string {
  if (status === 'submitted') return 'Der Assistent denkt nach…';
  // status === 'streaming' — pick the freshest part to derive the label.
  const parts = message.parts;
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part.type === 'text') {
      if (part.text.trim().length > 0) return 'Antwortet…';
      continue;
    }
    if (part.type === 'dynamic-tool') {
      if (part.state === 'output-available' || part.state === 'output-error') {
        return 'Plant nächsten Schritt…';
      }
      return `Werkzeug: ${part.toolName}…`;
    }
    if (part.type.startsWith('tool-')) {
      const toolPart = part as ToolPart;
      const isToolDone =
        toolPart.state === 'output-available' ||
        toolPart.state === 'output-error';
      if (isToolDone) return 'Plant nächsten Schritt…';
      const titles =
        toolPart.type !== 'dynamic-tool'
          ? TOOL_TITLES[toolPart.type]
          : undefined;
      return titles?.running ?? 'Verarbeitet…';
    }
  }
  return 'Der Assistent denkt nach…';
}

/** Live activity row pinned to the in-flight assistant message. */
function AgentActivityRow({
  message,
  status,
}: {
  message: AssistantUIMessage;
  status: ChatStatus;
}) {
  return (
    <div className="eos-asst-activity" aria-live="polite">
      <ActivityDots />
      <span className="eos-asst-activity-label">
        {activityLabel(message, status)}
      </span>
    </div>
  );
}

/** Counts tool parts the agent ran during a turn — used for the turn footer. */
function countSteps(message: AssistantUIMessage): number {
  return message.parts.reduce((total, part) => {
    if (part.type === 'dynamic-tool' || part.type.startsWith('tool-')) {
      const toolPart = part as ToolPart;
      return toolPart.state === 'output-available' ||
        toolPart.state === 'output-error'
        ? total + 1
        : total;
    }
    return total;
  }, 0);
}

/** Formats a wall-clock duration as a compact German label (e.g. "3,2 s"). */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(1, Math.round(ms / 100) * 100)} ms`;
  const seconds = ms / 1000;
  return seconds < 10
    ? `${seconds.toFixed(1).replace('.', ',')} s`
    : `${Math.round(seconds)} s`;
}

/** German-grouped integer (e.g. 4_321 → "4.321"). */
function formatTokens(value: number): string {
  return Math.round(value).toLocaleString('de-DE');
}

/**
 * Formats an estimated USD cost. Tiny values get more decimals so a sub-cent
 * turn doesn't read as "$0.00".
 */
function formatCostUsd(usd: number): string {
  if (usd >= 0.1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.001) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(4)}`;
}

/** End-of-turn footer rendered under a completed assistant message. */
function TurnFooter({
  message,
  durationMs,
  conversationId,
}: {
  message: AssistantUIMessage;
  /** Wall-clock duration for fresh turns; `null` when the row was hydrated from history. */
  durationMs: number | null;
  conversationId: string | null;
}) {
  const steps = countSteps(message);
  const meta = message.metadata;
  const totalTokens = meta?.totalTokens;
  const inputTokens = meta?.inputTokens;
  const outputTokens = meta?.outputTokens;
  const cacheReadTokens = meta?.cacheReadTokens;
  const reasoningTokens = meta?.reasoningTokens;
  const costUsd = meta?.costUsd;

  // Build a single descriptive breakdown for the title attribute so users can
  // hover the token count and see how it splits across input / output / cache.
  const breakdown = [
    inputTokens != null ? `Eingabe: ${formatTokens(inputTokens)}` : null,
    outputTokens != null ? `Ausgabe: ${formatTokens(outputTokens)}` : null,
    cacheReadTokens != null && cacheReadTokens > 0
      ? `Cache-Treffer: ${formatTokens(cacheReadTokens)}`
      : null,
    reasoningTokens != null && reasoningTokens > 0
      ? `Reasoning: ${formatTokens(reasoningTokens)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="eos-asst-turn-foot" aria-live="polite">
      <span className="eos-asst-turn-foot-check" aria-hidden="true">
        ✓
      </span>
      <span>Fertig</span>
      {durationMs != null && (
        <>
          <span className="eos-asst-turn-foot-sep" aria-hidden="true">
            ·
          </span>
          <span className="eos-asst-mono">{formatDuration(durationMs)}</span>
        </>
      )}
      {steps > 0 && (
        <>
          <span className="eos-asst-turn-foot-sep" aria-hidden="true">
            ·
          </span>
          <span>
            {steps} {steps === 1 ? 'Schritt' : 'Schritte'}
          </span>
        </>
      )}
      {totalTokens != null && (
        <>
          <span className="eos-asst-turn-foot-sep" aria-hidden="true">
            ·
          </span>
          <span
            className="eos-asst-mono"
            title={breakdown || undefined}
          >
            {formatTokens(totalTokens)} Tokens
          </span>
        </>
      )}
      {costUsd != null && (
        <>
          <span className="eos-asst-turn-foot-sep" aria-hidden="true">
            ·
          </span>
          <span className="eos-asst-mono" title="Geschätzte Modellkosten">
            {formatCostUsd(costUsd)}
          </span>
        </>
      )}
      {meta?.messageId && (
        <AssistantRating
          key={meta.messageId}
          messageId={meta.messageId}
          conversationId={conversationId}
          initialRating={meta.rating ?? null}
        />
      )}
    </div>
  );
}

/** Resolves the tone of a tool part from its lifecycle state. */
function toolTone(part: ToolPart): ToolTone {
  if (part.state === 'output-error') return 'error';
  if (part.state === 'output-available') return 'done';
  return 'running';
}

/** Short detail line for a tool part — what it is looking up. */
function toolDetail(part: ToolPart): string | undefined {
  switch (part.type) {
    case 'tool-searchItems':
      return part.input?.query ? `„${part.input.query}“` : undefined;
    case 'tool-getItem':
      return part.input?.itemId ? `#${part.input.itemId}` : undefined;
    case 'tool-getBusinessPartner':
    case 'tool-getBusinessPartnerOrders':
    case 'tool-listBusinessPartnerContacts':
      return part.input?.businessPartnerId != null
        ? `#${part.input.businessPartnerId}`
        : undefined;
    case 'tool-getOrder':
      return part.input?.docNum != null ? `#${part.input.docNum}` : undefined;
    case 'tool-queryBusinessPartners': {
      const filters = part.input?.filters ?? [];
      const nameFilter = filters.find((filter) => filter?.field === 'name');
      if (nameFilter?.value != null) return `„${nameFilter.value}“`;
      const typeFilter = filters.find((filter) => filter?.field === 'type');
      if (typeFilter?.value != null) {
        const type = String(typeFilter.value).toLowerCase();
        return type.includes('sup') || type.includes('lief')
          ? 'Lieferanten'
          : 'Kunden';
      }
      return undefined;
    }
    case 'tool-activateSkill':
      return part.input?.name ? `„${part.input.name}“` : undefined;
    case 'tool-createContactList':
      return part.input?.name ? `„${part.input.name}“` : undefined;
    case 'tool-addContactsToContactList':
    case 'tool-addDefaultContactsToContactList':
      return part.input?.listId != null ? `#${part.input.listId}` : undefined;
    default:
      return undefined;
  }
}

/** Compact activity card for a tool the agent invoked. */
function ToolActivity({ part }: { part: ToolPart }) {
  const tone = toolTone(part);
  const title =
    part.type === 'dynamic-tool'
      ? `Werkzeug: ${part.toolName}`
      : TOOL_TITLES[part.type][tone];
  const detail = toolDetail(part);

  return (
    <div className={`eos-asst-tool is-${tone}`}>
      <span className="eos-asst-tool-icon" aria-hidden="true">
        {tone === 'running' ? (
          <ActivityDots />
        ) : tone === 'error' ? (
          '!'
        ) : (
          '✓'
        )}
      </span>
      <div className="eos-asst-tool-body">
        <span className="eos-asst-tool-title">{title}</span>
        {detail && <span className="eos-asst-tool-detail">{detail}</span>}
      </div>
    </div>
  );
}

/** Renders a single message part, or `null` for parts with no chat UI. */
function PartView({
  part,
  isUser,
  pendingAskUser,
  onSubmitAskUser,
}: {
  part: MessagePart;
  isUser: boolean;
  pendingAskUser: ReturnType<typeof useAssistantChat>['pendingAskUser'];
  onSubmitAskUser: ReturnType<typeof useAssistantChat>['submitAskUserAnswers'];
}) {
  switch (part.type) {
    case 'text':
      if (!part.text.trim()) return null;
      // User input stays literal; assistant replies are streamed Markdown.
      return isUser ? (
        <div className="eos-asst-bubble">{part.text}</div>
      ) : (
        <div className="eos-asst-bubble eos-asst-md">
          <Streamdown controls={false}>{part.text}</Streamdown>
        </div>
      );
    case 'tool-askUser':
      if (
        pendingAskUser &&
        part.state !== 'output-available' &&
        part.state !== 'output-error' &&
        pendingAskUser.toolCallId === part.toolCallId
      ) {
        return (
          <AssistantAskUser
            input={pendingAskUser.input}
            onSubmit={onSubmitAskUser}
          />
        );
      }
      if (part.state === 'output-available') {
        return <AssistantAskUserAnswered output={part.output} />;
      }
      return <ToolActivity part={part} />;
    case 'tool-searchItems':
    case 'tool-getItem':
    case 'tool-queryBusinessPartners':
    case 'tool-getBusinessPartner':
    case 'tool-getBusinessPartnerOrders':
    case 'tool-getOrder':
    case 'tool-listBusinessPartnerContacts':
      // While the tool runs, show the activity card; once it has output,
      // render the result as a rich card instead of plain Markdown.
      return part.state === 'output-available' ? (
        <AssistantToolResult part={part} />
      ) : (
        <ToolActivity part={part} />
      );
    case 'tool-listContactLists':
    case 'tool-createContactList':
    case 'tool-addContactsToContactList':
    case 'tool-addDefaultContactsToContactList':
      // Verteiler tools render a generic activity card during the call. The
      // agent summarises the outcome in its trailing Markdown reply, so no
      // dedicated result card is needed — the activity card stays as the
      // visible trace.
      return <ToolActivity part={part} />;
    case 'tool-message_compose_v1':
      // Client-rendered composer card. As soon as the input has streamed in,
      // show the card — the tool has no execute and never produces output.
      if (
        part.state === 'input-available' ||
        part.state === 'output-available'
      ) {
        return <MessageComposeCard input={part.input} />;
      }
      return <ToolActivity part={part} />;
    case 'tool-activateSkill':
    case 'dynamic-tool':
      return <ToolActivity part={part} />;
    default:
      return null;
  }
}

/**
 * Decides whether to render the end-of-turn footer for an assistant message.
 * Fresh turns get one as soon as `turnDurationsMs` records the wall-clock
 * length; hydrated history rows get one as soon as the row has been persisted
 * (we key off `metadata.messageId`, which is only set after the backend
 * stored the row).
 */
function shouldShowFooter(
  message: AssistantUIMessage,
  turnDurationMs: number | undefined,
): boolean {
  if (turnDurationMs != null) return true;
  return Boolean(message.metadata?.messageId);
}

/** True when a message has at least one part the chat actually renders. */
function hasRenderablePart(message: AssistantUIMessage): boolean {
  return message.parts.some(
    (part) =>
      (part.type === 'text' && part.text.trim().length > 0) ||
      part.type === 'tool-searchItems' ||
      part.type === 'tool-getItem' ||
      part.type === 'tool-queryBusinessPartners' ||
      part.type === 'tool-getBusinessPartner' ||
      part.type === 'tool-getBusinessPartnerOrders' ||
      part.type === 'tool-getOrder' ||
      part.type === 'tool-listBusinessPartnerContacts' ||
      part.type === 'tool-askUser' ||
      part.type === 'tool-activateSkill' ||
      part.type === 'tool-message_compose_v1' ||
      part.type === 'tool-listContactLists' ||
      part.type === 'tool-createContactList' ||
      part.type === 'tool-addContactsToContactList' ||
      part.type === 'tool-addDefaultContactsToContactList' ||
      part.type === 'dynamic-tool',
  );
}

function ChatMessage({
  message,
  pendingAskUser,
  onSubmitAskUser,
  isLiveAssistant,
  status,
  turnDurationMs,
  conversationId,
}: {
  message: AssistantUIMessage;
  pendingAskUser: ReturnType<typeof useAssistantChat>['pendingAskUser'];
  onSubmitAskUser: ReturnType<typeof useAssistantChat>['submitAskUserAnswers'];
  /** True when this is the most recent assistant message *and* still streaming. */
  isLiveAssistant: boolean;
  status: ChatStatus;
  /** Duration of the completed turn ending in this message, if any. */
  turnDurationMs: number | undefined;
  /** Persisted conversation id — keys the rating mutation's invalidation. */
  conversationId: string | null;
}) {
  const isUser = message.role === 'user';
  return (
    <article className={`eos-asst-msg ${isUser ? 'is-user' : 'is-assistant'}`}>
      {!isUser && (
        <span className="eos-asst-msg-avatar" aria-hidden="true">
          <AgentMark />
        </span>
      )}
      <div className="eos-asst-msg-main">
        <span className="eos-asst-msg-who">
          {isUser ? 'Du' : 'EOS Assistent'}
        </span>
        {message.parts.map((part, index) => (
          <PartView
            key={`${message.id}-${index}`}
            part={part}
            isUser={isUser}
            pendingAskUser={pendingAskUser}
            onSubmitAskUser={onSubmitAskUser}
          />
        ))}
        {isLiveAssistant && (
          <AgentActivityRow message={message} status={status} />
        )}
        {!isUser && !isLiveAssistant && shouldShowFooter(message, turnDurationMs) && (
          <TurnFooter
            message={message}
            durationMs={turnDurationMs ?? null}
            conversationId={conversationId}
          />
        )}
      </div>
    </article>
  );
}

interface ComposerProps {
  value: string;
  busy: boolean;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
}

function Composer({
  value,
  busy,
  inputRef,
  onChange,
  onSubmit,
  onStop,
}: ComposerProps) {
  // Auto-grow the textarea up to a capped height.
  useLayoutEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`;
  }, [value, inputRef]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!busy) onSubmit();
    }
  };

  return (
    <div className="eos-asst-composer">
      <textarea
        ref={inputRef}
        className="eos-asst-input"
        rows={1}
        value={value}
        placeholder="Frag den EOS Assistenten…"
        aria-label="Nachricht an den Assistenten"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {busy ? (
        <button
          type="button"
          className="eos-asst-send is-stop"
          aria-label="Generierung stoppen"
          onClick={onStop}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect
              x="4.5"
              y="4.5"
              width="7"
              height="7"
              rx="1"
              fill="currentColor"
            />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className="eos-asst-send"
          aria-label="Senden"
          disabled={!value.trim()}
          onClick={onSubmit}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 13.2V3.4M8 3.4 3.6 7.8M8 3.4l4.4 4.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * The EOS Assistant slide-over — a right-hand drawer with a streaming chat,
 * available on every internal page. Rendered once by the app shell and toggled
 * via the {@link useAssistantStore}. Kept mounted so the conversation survives
 * closing and reopening the panel.
 *
 * Backed by the real `/v1/ai/chat` agent — see {@link useAssistantChat}.
 */
export function AssistantPanel() {
  const isOpen = useAssistantStore((state) => state.isOpen);
  const isExpanded = useAssistantStore((state) => state.isExpanded);
  const isHistoryOpen = useAssistantStore((state) => state.isHistoryOpen);
  const close = useAssistantStore((state) => state.close);
  const toggleExpanded = useAssistantStore((state) => state.toggleExpanded);
  const toggleHistory = useAssistantStore((state) => state.toggleHistory);
  const {
    messages,
    status,
    error,
    isBusy,
    conversationId,
    pendingAskUser,
    turnDurationsMs,
    send,
    submitAskUserAnswers,
    stop,
    retry,
    newConversation,
    loadConversation,
  } = useAssistantChat();

  const [composerValue, setComposerValue] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Close on Escape while the panel is open.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // Focus the composer when the panel opens.
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Keep the newest message in view as the conversation streams.
  useLayoutEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [messages, status]);

  const visibleMessages = messages.filter(hasRenderablePart);
  const hasConversation = visibleMessages.length > 0;

  // The freshest assistant message gets the live activity row while busy. The
  // standalone thinking bubble only appears before the server has streamed any
  // assistant message at all.
  const lastAssistantId = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant')?.id;
  const showStandaloneThinking = isBusy && lastAssistantId == null;

  const submitComposer = () => {
    const text = composerValue.trim();
    if (!text || isBusy) return;
    send(text);
    setComposerValue('');
  };

  const sendPrompt = (prompt: string) => {
    if (isBusy) return;
    send(prompt);
  };

  return (
    <div
      className={`eos-asst-root${isOpen ? ' is-open' : ''}${
        isExpanded ? ' is-wide' : ''
      }`}
      inert={!isOpen}
    >
      <button
        type="button"
        className="eos-asst-backdrop"
        aria-label="Assistent schließen"
        tabIndex={-1}
        onClick={close}
      />

      <aside
        className="eos-asst-panel"
        role="dialog"
        aria-modal="true"
        aria-label="EOS Assistent"
      >
        {isHistoryOpen && (
          <AssistantHistorySidebar
            activeConversationId={conversationId}
            onNewConversation={() => {
              newConversation();
              setComposerValue('');
            }}
            onSelectConversation={(id, persistedMessages) => {
              loadConversation(id, persistedMessages);
              setComposerValue('');
            }}
            onActiveDeleted={() => {
              newConversation();
              setComposerValue('');
            }}
          />
        )}
        <div className="eos-asst-main">
        <header className="eos-asst-head">
          <span className="eos-asst-mark">
            <AgentMark />
          </span>
          <span className="eos-asst-id">
            <span className="eos-asst-name">EOS Assistent</span>
            <span className="eos-asst-role">Dein Arbeits-Copilot</span>
          </span>
          <button
            type="button"
            className="eos-asst-icon-btn"
            aria-label={isHistoryOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
            aria-pressed={isHistoryOpen}
            title={isHistoryOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
            onClick={toggleHistory}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M2.5 4h11M2.5 8h11M2.5 12h11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="eos-asst-icon-btn"
            aria-label={isExpanded ? 'Schmaler anzeigen' : 'Breiter anzeigen'}
            aria-pressed={isExpanded}
            title={isExpanded ? 'Schmaler' : 'Breiter'}
            onClick={toggleExpanded}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d={
                  isExpanded
                    ? 'M9 3.5 13.5 8 9 12.5M3.5 3.5 8 8 3.5 12.5'
                    : 'M7 3.5 2.5 8 7 12.5M12.5 3.5 8 8 12.5 12.5'
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="eos-asst-icon-btn"
            aria-label="Schließen"
            onClick={close}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div
          className="eos-asst-thread ds-scroll"
          ref={threadRef}
          role="log"
          aria-live="polite"
          aria-label="Konversation mit dem Assistenten"
        >
          {!hasConversation && (
            <div className="eos-asst-empty">
              <span className="eos-asst-empty-mark">
                <AgentMark />
              </span>
              <p className="eos-asst-empty-text">
                Hallo! Ich kann den EOS-Katalog und Geschäftspartner für dich
                durchsuchen — Artikel, Preise, Lagerbestände, Partner und ihre
                Bestellungen. Stell mir eine Frage oder wähle einen Vorschlag.
              </p>
            </div>
          )}

          {visibleMessages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              pendingAskUser={pendingAskUser}
              onSubmitAskUser={submitAskUserAnswers}
              isLiveAssistant={
                isBusy &&
                message.role === 'assistant' &&
                message.id === lastAssistantId
              }
              status={status}
              turnDurationMs={turnDurationsMs[message.id]}
              conversationId={conversationId}
            />
          ))}

          {showStandaloneThinking && <ThinkingBubble />}

          {status === 'error' && (
            <div className="eos-asst-error" role="alert">
              <span className="eos-asst-error-text">
                {error?.message ?? 'Der Assistent ist nicht erreichbar.'}
              </span>
              <button
                type="button"
                className="eos-asst-ghost"
                onClick={retry}
              >
                Erneut versuchen
              </button>
            </div>
          )}
        </div>

        <div className="eos-asst-foot">
          {!hasConversation && (
            <div className="eos-asst-suggest">
              {ASSISTANT_SUGGESTIONS.map((suggestion, index) => (
                <button
                  key={suggestion.label}
                  type="button"
                  className="eos-asst-suggest-card"
                  style={{ '--i': index } as CSSProperties}
                  disabled={isBusy}
                  onClick={() => sendPrompt(suggestion.prompt)}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          )}
          <Composer
            value={composerValue}
            busy={isBusy}
            inputRef={inputRef}
            onChange={setComposerValue}
            onSubmit={submitComposer}
            onStop={stop}
          />
        </div>
        </div>
      </aside>
    </div>
  );
}
